import React, { useState } from 'react'
import RdnaSection from '@ringdna/common/src/components/RdnaFormElements/RdnaFormLayout/RdnaSection'
import RdnaDrawer from '@ringdna/common/src/components/RdnaDrawer'
import RdnaTextInput from '@ringdna/common/src/components/RdnaFormElements/RdnaTextInput'
import RdnaButton from '@ringdna/common/src/components/RdnaButton'
import { Footer } from '../../../roles/styles'
import RdnaFilePicker from '@ringdna/common/src/components/RdnaFilePicker'
import { useComplianceAnnouncementsCreate, useComplianceAnnouncementsUpdate } from '../../api'
import { DESCRIPTION, NAME, TARGETING, UPLOAD } from './const'
import { AnnouncementTargetingType, ComplianceAnnouncement, TargetingObject } from '../../types'
import { getErrorMessage } from '@ringdna/common/src/utils/errorUtils'
import { useRdnaNotification } from '@ringdna/common/src/components/RdnaNotification/RdnaNotificationContext'
import RdnaForm from '@ringdna/common/src/components/RdnaFormElements'
import { isEmpty } from 'lodash-es'
import { getRules } from '@ringdna/common/src/components/RdnaFormElements/_helpers'
import Targeting from './Targeting'

interface Props {
  accountId: number
  open: boolean
  onClose: (isFetch: boolean) => void
  complianceAnnouncement: ComplianceAnnouncement
}

export default function ComplianceAnnouncementPanel({ accountId, open, onClose, complianceAnnouncement }: Props) {
  const [file, setFile] = useState<File | null>()
  const [isProcessing, setIsProcessing] = useState<boolean>(false)
  const { error } = useRdnaNotification()
  const createComplianceAnnouncement = useComplianceAnnouncementsCreate()
  const updateComplianceAnnouncement = useComplianceAnnouncementsUpdate()

  const submitForm = async (data: ComplianceAnnouncement) => {
    setIsProcessing(true)

    const body = {
      'complianceAnnouncement.account.id': accountId,
      'complianceAnnouncement.name': data.name,
      'complianceAnnouncement.description': data.description,
      'complianceAnnouncement.targetingType': data.targetingType,
      audioFile: file
    }
    data.targetObjects?.map(
      (item: TargetingObject, index: number) =>
      // @ts-ignore
        (body[`targetObjects[${index}]`] =
          data.targetingType === AnnouncementTargetingType.SALESFORCE_ROLES ? item.name : item.id.toString())
    )
    data.excludeUsers?.map((item: TargetingObject, index: number) => {
      // @ts-ignore
      body[`excludeUsers[${index}][id]`] = item.id
    })
    try {
      if (complianceAnnouncement.id) {
        await updateComplianceAnnouncement({
          complianceAnnouncementId: complianceAnnouncement.id,
          body: {
            ...body,
            'complianceAnnouncement.id': complianceAnnouncement.id
          }
        })
      } else {
        await createComplianceAnnouncement({
          body: body
        })
      }
    } catch (e) {
      error(getErrorMessage(e))
    } finally {
      setIsProcessing(false)
      onClose(true)
    }
  }

  return (
    <RdnaDrawer
      open={open}
      onClose={() => onClose(false)}
      heading={`${complianceAnnouncement.id ? 'Edit' : 'Add'} Compliance Announcement`}
    >
      <RdnaForm<ComplianceAnnouncement> mode="all" defaultValues={complianceAnnouncement} onValid={submitForm}>
        {(
          { handleSubmit, watch, formState: { isDirty, errors }, setValue, getValues },
          { RdnaFormController, getMuiProps }
        ) => {
          return (
            <>
              <RdnaSection sectionTitle={NAME.TITLE} subtitle={NAME.SUBTITLE}>
                <RdnaFormController
                  name="name"
                  rules={getRules(['required', 'notAllWhiteSpace'])}
                  render={props => (
                    <RdnaTextInput
                      required
                      {...getMuiProps(props)}
                      placeholder={NAME.TITLE}
                      onChange={e => setValue('name', e.target.value, { shouldDirty: true })}
                    />
                  )}
                />
              </RdnaSection>
              <RdnaSection sectionTitle={DESCRIPTION.TITLE} subtitle={DESCRIPTION.SUBTITLE}>
                <RdnaFormController
                  name="description"
                  rules={getRules(['required', 'notAllWhiteSpace'])}
                  render={props => (
                    <RdnaTextInput
                      required
                      {...getMuiProps(props)}
                      placeholder={DESCRIPTION.TITLE}
                      onChange={e => setValue('description', e.target.value, { shouldDirty: true })}
                    />
                  )}
                />
              </RdnaSection>
              <RdnaSection sectionTitle={TARGETING.TITLE} subtitle={TARGETING.SUBTITLE}>
                <Targeting
                  excludeOpen={watch('excludeUsers')?.length > 0}
                  accountId={accountId}
                  isSupervisorAgent={false}
                />
              </RdnaSection>
              <RdnaSection sectionTitle={UPLOAD.TITLE} subtitle={UPLOAD.SUBTITLE}>
                <RdnaFormController
                  name="audioUrl"
                  render={props => (
                    <RdnaFilePicker
                      fileType=".mp3"
                      name="picker"
                      label={UPLOAD.LABEL_BUTTON}
                      existingFile={getMuiProps(props).value}
                      onChange={e => setFile(e.fileBinary)}
                      mbFileSizeLimit={10}
                      onError={e => error(e)}
                    />
                  )}
                />
              </RdnaSection>
              <Footer>
                <RdnaButton
                  text={'Cancel'}
                  color="neutral"
                  variant="text"
                  type={'reset'}
                  onClick={() => onClose(false)}
                />
                <RdnaButton
                  text={'Save'}
                  onClick={handleSubmit}
                  type={'submit'}
                  disabled={
                    !watch('name') ||
                    !watch('description') ||
                    !((complianceAnnouncement.id && isDirty) || file) ||
                    !isEmpty(errors)
                  }
                  processing={isProcessing}
                ></RdnaButton>
              </Footer>
            </>
          )
        }}
      </RdnaForm>
    </RdnaDrawer>
  )
}
